<template>
  <div>
    <v-alert v-if="!usuarioLogado.adminWeb" type="warning" outlined dense class="mb-4">
      Acesso restrito: faça login como administrador para consultar o relatório de nota final.
    </v-alert>
    <v-container v-else fluid class="relatorio-container">
      <v-card class="pa-5 mb-6" elevation="2">
        <v-card-title class="font-weight-bold">Relatório de Nota Final</v-card-title>
        <v-row align="center">
          <v-col cols="12" md="8">
            <v-text-field
              v-model.number="idProcesso"
              label="ID do Processo"
              type="number"
              hint="Ex.: 4"
              persistent-hint
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-btn color="primary" block :loading="loading" :disabled="!idProcesso" @click="buscar">
              <v-icon left>mdi-magnify</v-icon>
              Buscar
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <template v-if="relatorio.length">
        <v-row class="mb-2">
          <v-col cols="6" md="3">
            <v-card elevation="2" class="pa-4 text-center resumo-card">
              <div class="text-h5 font-weight-bold primary--text">{{ totalInstituicoes }}</div>
              <div class="text--secondary body-2">Instituições</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card elevation="2" class="pa-4 text-center resumo-card">
              <div class="text-h5 font-weight-bold" :class="`${corNota(mediaGeral)}--text`">{{ mediaGeral.toFixed(2) }}</div>
              <div class="text--secondary body-2">Média geral</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card elevation="2" class="pa-4 text-center resumo-card">
              <div class="text-h5 font-weight-bold success--text">{{ maiorNota.toFixed(2) }}</div>
              <div class="text--secondary body-2">Maior nota</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card elevation="2" class="pa-4 text-center resumo-card">
              <div class="text-h5 font-weight-bold error--text">{{ menorNota.toFixed(2) }}</div>
              <div class="text--secondary body-2">Menor nota</div>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <v-card elevation="2" class="pa-4">
        <v-card-title class="font-weight-bold d-flex align-center px-0 flex-wrap">
          Notas por Instituição
          <v-spacer />
          <div style="max-width: 260px" class="mr-3">
            <v-text-field
              v-model="busca"
              label="Buscar instituição"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              clearable
              hide-details
            ></v-text-field>
          </div>
          <v-btn color="primary" small :disabled="!linhasTabela.length" @click="exportarPdf">
            <v-icon left small>mdi-file-pdf-box</v-icon>
            Exportar PDF
          </v-btn>
        </v-card-title>

        <v-data-table
          v-if="linhasTabela.length"
          :headers="headersTabela"
          :items="linhasTabela"
          :loading="loading"
          sort-by="nota_final"
          sort-desc
          disable-pagination
          hide-default-footer
          dense
        >
          <template v-slot:item.nota_final="{ item }">
            <v-chip :color="corNota(item.nota_final)" dark small>{{ Number(item.nota_final).toFixed(2) }}</v-chip>
          </template>
        </v-data-table>

        <v-card-text v-else-if="loading" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        </v-card-text>
        <v-card-text v-else-if="buscaRealizada" class="text-center text--secondary pa-8">
          Nenhum resultado encontrado para essa busca.
        </v-card-text>
        <v-card-text v-else class="text-center text--secondary pa-8"> Selecione o processo e clique em Buscar. </v-card-text>
      </v-card>
    </v-container>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000" top>{{ snackbarText }}</v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import AvaliacaoInstitucionalServices from "@/services/avaliacaoInstitucional/AvaliacaoInstitucionalServices";

export default {
  name: "RelatorioNotaFinal",
  data: () => ({
    idProcesso: null,
    legenda: {},
    relatorio: [],
    busca: "",
    loading: false,
    buscaRealizada: false,
    snackbar: false,
    snackbarText: "",
    snackbarColor: "success",
  }),
  computed: {
    ...mapGetters({
      usuarioLogado: "config/getUsuarioLogado",
    }),
    colunasQuestionario() {
      return Object.keys(this.legenda)
        .sort((a, b) => Number(a) - Number(b))
        .map((id) => ({ id, nome: this.legenda[id] }));
    },
    headersTabela() {
      return [
        { text: "Instituição", value: "instituicao" },
        ...this.colunasQuestionario.map((c) => ({ text: c.nome, value: c.id, align: "center", sortable: false })),
        { text: "Nota Final", value: "nota_final", align: "center" },
      ];
    },
    totalInstituicoes() {
      return this.relatorio.length;
    },
    mediaGeral() {
      if (!this.relatorio.length) return 0;
      const soma = this.relatorio.reduce((acc, i) => acc + Number(i.nota_final || 0), 0);
      return soma / this.relatorio.length;
    },
    maiorNota() {
      if (!this.relatorio.length) return 0;
      return Math.max(...this.relatorio.map((i) => Number(i.nota_final || 0)));
    },
    menorNota() {
      if (!this.relatorio.length) return 0;
      return Math.min(...this.relatorio.map((i) => Number(i.nota_final || 0)));
    },
    relatorioFiltrado() {
      let lista = this.relatorio;
      if (this.busca) {
        const termo = this.busca.toLowerCase();
        lista = lista.filter((i) => i.instituicao.toLowerCase().includes(termo));
      }
      return lista;
    },
    linhasTabela() {
      return this.relatorioFiltrado.map((r) => {
        const linha = { instituicao: r.instituicao, nota_final: Number(r.nota_final || 0) };
        this.colunasQuestionario.forEach((c) => {
          linha[c.id] = this.formatarNota(r[c.id]);
        });
        return linha;
      });
    },
  },
  methods: {
    formatarNota(valor) {
      const n = Number(valor);
      return n ? n.toFixed(2) : "—";
    },
    corNota(nota) {
      const v = Number(nota);
      if (v >= 8) return "success";
      if (v >= 6) return "warning";
      return "error";
    },
    async buscar() {
      if (!this.idProcesso) return;
      this.loading = true;
      this.buscaRealizada = false;
      try {
        const resp = await AvaliacaoInstitucionalServices.relatorioNotaFinal(this.idProcesso);
        this.legenda = resp?.data?.legenda || {};
        this.relatorio = resp?.data?.relatorio || [];
      } catch (error) {
        this.legenda = {};
        this.relatorio = [];
        this.exibirMensagem(error?.response?.data?.error || "Erro ao carregar relatório.", "error");
      } finally {
        this.loading = false;
        this.buscaRealizada = true;
      }
    },
    exportarPdf() {
      if (!this.linhasTabela.length) return;
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const dataAtual = new Date().toLocaleString("pt-BR");

      doc.setFontSize(14);
      doc.text("Relatório de Nota Final", 40, 40);
      doc.setFontSize(10);
      doc.text(`Processo: ${this.idProcesso}`, 40, 58);
      doc.text(`Emitido em ${dataAtual}`, 40, 72);

      const head = ["Instituição", ...this.colunasQuestionario.map((c) => c.nome), "Nota Final"];
      const body = [...this.linhasTabela]
        .sort((a, b) => b.nota_final - a.nota_final)
        .map((linha) => [
          linha.instituicao,
          ...this.colunasQuestionario.map((c) => linha[c.id]),
          linha.nota_final.toFixed(2),
        ]);

      doc.autoTable({
        startY: 88,
        head: [head],
        body,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [25, 118, 210] },
        margin: { left: 40, right: 40 },
      });

      const arquivo = `Relatorio_Nota_Final_Processo_${this.idProcesso}.pdf`;
      doc.save(arquivo);
    },
    exibirMensagem(texto, cor) {
      this.snackbarText = texto;
      this.snackbarColor = cor;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.relatorio-container {
  padding: 16px;
}

.resumo-card {
  border-radius: 12px !important;
}

@media (min-width: 1264px) {
  .relatorio-container {
    padding-left: 40px;
    padding-right: 40px;
  }
}
</style>
